import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { sendEmail, emailTemplates } from '../../utils/emailService';
import recaptchaConfig from '../../config/recaptcha';
import FocusTrap from '../../utils/focusTrap';
import './ClientReviewPage.css';

const ClientReviewPage = () => {
  // State to track completed reviews
  const [completedReviews, setCompletedReviews] = useState(() => {
    const stored = localStorage.getItem('rc-roofing-completed-reviews');
    return stored ? JSON.parse(stored) : {};
  });

  // Check for return from review platform
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const completedPlatform = urlParams.get('review_completed');
    
    if (completedPlatform) {
      setCompletedReviews(prev => {
        const updated = { ...prev, [completedPlatform]: true };
        localStorage.setItem('rc-roofing-completed-reviews', JSON.stringify(updated));
        
        // Send email notification for review completion
        const platformName = completedPlatform.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        sendReviewCompletionEmail(platformName);
        
        // Remove the parameter from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return updated;
      });
    }
  }, []);

  // Update showAllReviews when completion count changes
  useEffect(() => {
    const currentCompletedCount = Object.keys(completedReviews).filter(key => completedReviews[key]).length;
    
    if (currentCompletedCount >= 3) {
      // Just reached 3 reviews, hide remaining and show celebration
      setShowAllReviews(false);
    } else {
      // Less than 3 reviews, show all
      setShowAllReviews(true);
    }
  }, [completedReviews]);


  // Helper function to check if a specific review is completed
  const isReviewCompleted = (platformName) => {
    const platformKey = platformName.toLowerCase().replace(/\s+/g, '_');
    return completedReviews[platformKey] === true;
  };

  // Helper function to get all completed platform names
  const getCompletedPlatformNames = () => {
    return Object.keys(completedReviews).filter(key => completedReviews[key]);
  };

  // Helper function to get completion status for all platforms
  const getAllCompletionStatus = () => {
    const status = {};
    reviewPlatforms.forEach(platform => {
      const platformKey = platform.name.toLowerCase().replace(/\s+/g, '_');
      status[platform.name] = completedReviews[platformKey] === true;
    });
    return status;
  };


  // Email notification functions
  const sendReviewCompletionEmail = async (platformName, userInfo = {}) => {
    try {
      const emailData = emailTemplates.reviewCompletion({
        platformName,
        timestamp: new Date().toLocaleString(),
        userAgent: navigator.userAgent,
        pageUrl: window.location.href,
        completedCount,
        hasMinimumReviews,
        completedPlatforms: getCompletedPlatformNames(),
        ipAddress: userInfo.ip || 'Not available'
      });

      // Send email using the professional email service
      const result = await sendEmail(emailData, 'auto');
      
      if (process.env.NODE_ENV === 'development') {
      }
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error sending review completion email:', error);
      }
      // Fallback to mailto if all else fails
      const subject = `Review Confirmation - ${userInfo.name || 'Unknown Client'} (${completedCount} reviews)`;
      const body = `Review completed on ${platformName} at ${new Date().toLocaleString()}\n\nClient: ${userInfo.name || 'Unknown'}\nPhone: ${userInfo.phone || 'Not provided'}\nPlatform: ${platformName}\nTotal Reviews: ${completedCount}\n\nPlease contact client to send their Amazon gift card.`;
      
      const mailtoLink = `mailto:hi@rcroof.co?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink);
    }
  };


  // Check if 3 or more reviews are completed
  const completedCount = Object.keys(completedReviews).filter(key => completedReviews[key]).length;
  const hasMinimumReviews = completedCount >= 3;
  
  // State to show/hide remaining reviews
  const [showAllReviews, setShowAllReviews] = useState(!hasMinimumReviews);
  
  
  // State for notifications
  const [notification, setNotification] = useState(null);
  
  // State for review confirmation
  const [selectedReviews, setSelectedReviews] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [clientDetails, setClientDetails] = useState({
    name: '',
    phone: ''
  });
  
  // State for reCAPTCHA
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState('');
  
  // State for loading overlay
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State for redirect notification
  const [showRedirectOverlay, setShowRedirectOverlay] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  
  // Refs for focus management
  const modalRef = useRef(null);
  const focusTrapRef = useRef(null);
  const lastFocusedElementRef = useRef(null);

  // Show notification function
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Handle review selection
  const handleReviewSelection = (platformName, isSelected) => {
    setSelectedReviews(prev => ({
      ...prev,
      [platformName]: isSelected
    }));
  };

  // Get count of selected reviews
  const getSelectedCount = () => {
    return Object.values(selectedReviews).filter(Boolean).length;
  };

  // Handle confirmation button click
  const handleConfirmationClick = () => {
    if (getSelectedCount() >= 3) {
      // Store the currently focused element
      lastFocusedElementRef.current = document.activeElement;
      setShowDetailsModal(true);
    } else {
      showNotification('Please select at least 3 completed reviews', 'error');
    }
  };

  // Handle reCAPTCHA change
  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setRecaptchaError('');
  };

  // Handle reCAPTCHA error
  const handleRecaptchaError = () => {
    setRecaptchaError('reCAPTCHA verification failed. Please try again.');
  };

  // Modal focus management
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showDetailsModal && e.key === 'Escape') {
        handleModalClose();
      }
    };

    if (showDetailsModal) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Add escape key listener
      document.addEventListener('keydown', handleKeyDown);
      
      // Set up focus trap after modal is rendered
      setTimeout(() => {
        if (modalRef.current && !focusTrapRef.current) {
          focusTrapRef.current = new FocusTrap(modalRef.current);
          focusTrapRef.current.activate();
        }
      }, 0);
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Remove escape key listener
      document.removeEventListener('keydown', handleKeyDown);
      
      // Deactivate focus trap
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
        focusTrapRef.current = null;
      }
      
      // Return focus to the element that opened the modal
      if (lastFocusedElementRef.current) {
        lastFocusedElementRef.current.focus();
        lastFocusedElementRef.current = null;
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
        focusTrapRef.current = null;
      }
    };
  }, [showDetailsModal]);

  // Handle modal close
  const handleModalClose = () => {
    setShowDetailsModal(false);
  };

  // Input validation functions
  const validateName = (name) => {
    // Allow only letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    return nameRegex.test(name);
  };

  const validatePhone = (phone) => {
    // Allow only numbers, spaces, hyphens, parentheses, and plus sign
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    return phoneRegex.test(phone);
  };

  // Handle input changes with validation
  const handleNameChange = (e) => {
    const value = e.target.value;
    if (validateName(value) || value === '') {
      setClientDetails(prev => ({ ...prev, name: value }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Restrict to 10 characters max
    if (value.length <= 10 && (validatePhone(value) || value === '')) {
      setClientDetails(prev => ({ ...prev, phone: value }));
    }
  };

  // Handle client details submission
  const handleDetailsSubmit = async () => {
    // Validate required fields
    if (!clientDetails.name.trim() || !clientDetails.phone.trim()) {
      showNotification('Please fill in all required fields', 'error');
      // Focus on the first empty field
      if (!clientDetails.name.trim()) {
        const nameInput = document.getElementById('client-name');
        if (nameInput) {
          nameInput.focus();
          nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (!clientDetails.phone.trim()) {
        const phoneInput = document.getElementById('client-phone');
        if (phoneInput) {
          phoneInput.focus();
          phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    // Validate name format
    if (!validateName(clientDetails.name.trim())) {
      showNotification('Name can only contain letters, spaces, hyphens, and apostrophes', 'error');
      const nameInput = document.getElementById('client-name');
      if (nameInput) {
        nameInput.focus();
        nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Validate phone format
    if (!validatePhone(clientDetails.phone.trim())) {
      showNotification('Phone number can only contain numbers, spaces, hyphens, parentheses, and plus sign', 'error');
      const phoneInput = document.getElementById('client-phone');
      if (phoneInput) {
        phoneInput.focus();
        phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Validate minimum phone length
    const phoneDigits = clientDetails.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      showNotification('Phone number must be at least 10 digits', 'error');
      const phoneInput = document.getElementById('client-phone');
      if (phoneInput) {
        phoneInput.focus();
        phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!recaptchaValue) {
      setRecaptchaError('Please complete the reCAPTCHA verification');
      showNotification('Please complete the reCAPTCHA verification', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Send confirmation email with client details
      const selectedPlatforms = Object.keys(selectedReviews).filter(key => selectedReviews[key]);
      
      const emailData = {
        to: 'hi@rcroofing.com',
        subject: `Review Confirmation - ${clientDetails.name} (${getSelectedCount()} reviews)`,
        body: `
          <h2>Review Confirmation Request</h2>
          <p><strong>Client Name:</strong> ${clientDetails.name}</p>
          <p><strong>Phone Number:</strong> ${clientDetails.phone}</p>
          <p><strong>Selected Review Platforms:</strong></p>
          <ul>
            ${selectedPlatforms.map(platform => `<li>✅ ${platform}</li>`).join('')}
          </ul>
          <p><strong>Total Reviews Confirmed:</strong> ${getSelectedCount()}${getSelectedCount() > 3 ? ' (Above and beyond!)' : ''}</p>
          ${getSelectedCount() > 3 ? '<p><strong>Note:</strong> Client went above and beyond by leaving extra reviews!</p>' : ''}
          <p><strong>Timestamp:</strong> ${new Date().toLocaleString()}</p>
          <p><strong>Request:</strong> Please contact client to send their Amazon gift card.</p>
        `,
        // Template variables for email service
        client_name: clientDetails.name,
        client_phone: clientDetails.phone,
        review_count: getSelectedCount().toString(),
        review_platforms: selectedPlatforms.join(', '),
        recaptchaToken: recaptchaValue,
        type: 'review_confirmation' // This tells the email service to use the review template
      };

      await sendEmail(emailData, 'auto');
      
      showNotification('Thank you! A representative will contact you soon about your reward.', 'success');
      setShowDetailsModal(false);
      setClientDetails({ name: '', phone: '' });
      setSelectedReviews({});
      setRecaptchaValue(null);
      setRecaptchaError('');
      
      // Show redirect overlay after 3 seconds delay
      setTimeout(() => {
        setShowRedirectOverlay(true);
        setRedirectCountdown(5);
        
        // Start countdown
        const countdownInterval = setInterval(() => {
          setRedirectCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              // Redirect to main page
              window.location.href = '/';
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Clear overlay after 5 seconds
        setTimeout(() => {
          setShowRedirectOverlay(false);
          clearInterval(countdownInterval);
        }, 5000);
      }, 3000);
      
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      showNotification('Error submitting details. Please try again or contact us directly.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Return original URL since we removed tracking functionality
  const generateTrackingUrl = (originalUrl, platformName) => {
    return originalUrl;
  };

  const reviewPlatforms = [
    {
      name: 'Yelp',
      url: 'https://www.yelp.com/writeareview/biz/t_eiSjFuI-Pn4kSddqukDg',
      logo: '/images/review-logos/yelp.webp',
      color: '#ff1a1a'
    },
    {
      name: 'Google Reviews',
      url: 'https://www.google.com/maps/place//data=!4m3!3m2!1s0x8824cd505558d373:0x7e8ce49b14ad0d28!12e1?source=g.page.m.kd._&laa=lu-desktop-review-solicitation',
      logo: '/images/review-logos/google reviews.png',
      color: '#4285f4'
    },
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61574085062006&sk=reviews',
      logo: '/images/review-logos/facebook.png',
      color: '#1877f2'
    },
    {
      name: 'Houzz',
      url: 'https://www.houzz.com/writeProReview/cmd=r/n=webuser-231931539',
      logo: '/images/review-logos/houzzz.png',
      color: '#4caf50'
    },
    {
      name: 'Home Advisor',
      url: 'https://www.homeadvisor.com/rated.rc-roofing',
      logo: '/images/review-logos/home adivsor.png',
      color: '#00a651',
      disabled: true
    },
    {
      name: 'Nextdoor',
      url: 'https://nextdoor.com/pages/rc-roofing',
      logo: '/images/review-logos/nextdoor.png',
      color: '#00a86b',
      disabled: true
    },
    {
      name: "Angie's List",
      url: 'https://www.angi.com/companylist/us/il/chicago/rc-roofing-reviews.htm',
      logo: '/images/review-logos/angie\'s list.png',
      color: '#ff6600',
      disabled: true
    }
  ];

  return (
    <main className="client-review-page pad-h-xs-3 pad-h-sm-5 pad-h-6">
      <Helmet>
        <title>RC Roofing - Leave a Review</title>
        <meta name="description" content="Share your experience with RC Roofing by leaving a review on your preferred platform." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {notification.type === 'success' ? '✅' : '❌'}
            </span>
            <span className="notification-message">{notification.message}</span>
            <button 
              className="notification-close"
              onClick={() => setNotification(null)}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner-large"></div>
            <p className="loading-text">Submitting your details...</p>
            <p className="loading-subtext">Please wait while we process your request</p>
          </div>
        </div>
      )}

      {/* Redirect Overlay */}
      {showRedirectOverlay && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner-large"></div>
            <p className="loading-text">Redirecting you to the main page in {redirectCountdown} seconds</p>
            <p className="loading-subtext">
              or <button 
                className="go-now-link" 
                onClick={() => window.location.href = '/'}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#f8bc06', 
                  textDecoration: 'underline', 
                  cursor: 'pointer',
                  fontSize: 'inherit',
                  fontFamily: 'inherit'
                }}
              >
                click here to Go Now
              </button>
            </p>
          </div>
        </div>
      )}

      <div className="review-container">
        <header className="review-header">
          <div className="stars-container">
            <span className="star">⭐</span>
            <span className="star">⭐</span>
            <span className="star">⭐</span>
            <span className="star">⭐</span>
            <span className="star">⭐</span>
          </div>
          <h1>Thank You for Choosing<br /><span className="brand-highlight">RC Roofing</span></h1>
          <p className="review-subtitle">
            We hope you're thrilled with your project results. <br />Your feedback helps us continue providing exceptional service.
          </p>
        </header>

        <section className="incentive-section" aria-labelledby="incentive-title">
          <div className="incentive-card">
            <h2 id="incentive-title">🎁 Special Thank You Offer</h2>
            <div className="incentive-details">
            <p>
              Leave reviews on <strong>at least 3 of the platforms below</strong>, 
              and receive a <strong>free Amazon gift card</strong> as our way of saying thank you!
            </p>
            </div>
          </div>
        </section>

        <section className="review-platforms" aria-labelledby="platforms-title">
          <h2 id="platforms-title">Choose Your Preferred Platform</h2>
          
          {/* Celebration Effect */}
          {hasMinimumReviews && (
            <div className="celebration-container">
              <div className="celebration-message">
                <div className="party-popper">🎉</div>
                <h3>Congratulations!</h3>
                <p>You've completed {completedCount} reviews! You've earned your Amazon gift card reward!</p>
              </div>
            </div>
          )}

          <div className="platforms-grid">
            {reviewPlatforms.map((platform, index) => {
              const platformKey = platform.name.toLowerCase().replace(/\s+/g, '_');
              const isCompleted = completedReviews[platformKey];
              
              // Hide remaining reviews if 3+ are completed and showAllReviews is false
              if (hasMinimumReviews && !showAllReviews && !isCompleted) {
                return null;
              }
              
              return (
                <div
                  key={index}
                  className={`platform-card ${
                    isCompleted ? 'completed disabled' : 
                    platform.disabled ? 'disabled grayed-out' : 
                    'clickable'
                  }`}
                  style={{ '--platform-color': platform.color }}
                  onClick={!isCompleted && !platform.disabled ? () => {
                    window.open(generateTrackingUrl(platform.url, platform.name), '_blank', 'noopener,noreferrer');
                  } : undefined}
                  onKeyDown={!isCompleted && !platform.disabled ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      window.open(generateTrackingUrl(platform.url, platform.name), '_blank', 'noopener,noreferrer');
                    }
                  } : undefined}
                  tabIndex={!isCompleted && !platform.disabled ? 0 : -1}
                  role="button"
                  aria-label={isCompleted ? `${platform.name} review completed` : 
                             platform.disabled ? `${platform.name} review coming soon` : 
                             `Leave a review on ${platform.name}`}
                  aria-disabled={isCompleted || platform.disabled}
                >
                  <div className="platform-logo">
                    <img src={platform.logo} alt={`${platform.name} logo`} />
                    {isCompleted && (
                      <div className="completion-check">
                        <span className="checkmark">✓</span>
                      </div>
                    )}
                    {platform.disabled && (
                      <div className="disabled-overlay">
                        <span className="disabled-text">Coming Soon</span>
                      </div>
                    )}
                  </div>
                  <div className="review-button">
                    {isCompleted ? 'Review Completed!' : 
                     platform.disabled ? 'Coming Soon' : 
                     'Leave Review'}
                    <span className="arrow">{isCompleted ? '✓' : platform.disabled ? '⏳' : '→'}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show remaining reviews button */}
          {hasMinimumReviews && !showAllReviews && (
            <div className="show-remaining-section">
              <button 
                className="show-remaining-btn"
                onClick={() => setShowAllReviews(true)}
              >
                <span className="btn-icon">🎯</span>
                Want to leave more reviews? Show remaining platforms
              </button>
            </div>
          )}

          {/* Hide remaining reviews button */}
          {hasMinimumReviews && showAllReviews && (
            <div className="hide-remaining-section">
              <button 
                className="hide-remaining-btn"
                onClick={() => setShowAllReviews(false)}
              >
                <span className="btn-icon">🎉</span>
                You're all set! Hide remaining platforms
              </button>
            </div>
          )}
        </section>

        <section className="confirmation-section" aria-labelledby="confirmation-title">
          <h2 id="confirmation-title">Completed Your Reviews?</h2>
          <p>If you've left reviews on 3 or more platforms, let us know so we can send you your Amazon gift card!</p>
          
          {/* Review Selection */}
          <div className="review-selection">
            <h3>Select the platforms where you've completed reviews:</h3>
            <div className="review-checkboxes">
              {reviewPlatforms.map((platform, index) => (
                <div key={index} className="review-checkbox-item">
                  <label 
                    className="checkbox-label"
                    htmlFor={`review-checkbox-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}
                    onKeyDown={(e) => {
                      if ((e.key === ' ' || e.key === 'Enter') && !platform.disabled) {
                        e.preventDefault();
                        handleReviewSelection(platform.name, !selectedReviews[platform.name]);
                      }
                    }}
                    tabIndex={platform.disabled ? -1 : 0}
                    role="checkbox"
                    aria-checked={selectedReviews[platform.name] || false}
                    aria-disabled={platform.disabled}
                    aria-describedby={`checkbox-description-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <input
                      type="checkbox"
                      id={`review-checkbox-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}
                      checked={selectedReviews[platform.name] || false}
                      onChange={(e) => handleReviewSelection(platform.name, e.target.checked)}
                      disabled={platform.disabled}
                      tabIndex={-1}
                      aria-describedby={`checkbox-description-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    <span className="checkmark-custom" aria-hidden="true"></span>
                    <div className="platform-info">
                      <span className="platform-name">{platform.name}</span>
                      <span 
                        className={`platform-status ${platform.disabled ? 'disabled' : 'available'}`}
                        id={`checkbox-description-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {platform.disabled ? '⏳ Coming Soon' : '✅ Available'}
                      </span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
            
              <div className="selection-summary">
                <p className='margin-b-0'>Selected Reviews: <strong>{getSelectedCount()}{getSelectedCount() >= 4 ? '+' : ''}/3 minimum required</strong></p>
                <p className="review-note">(Only 3 reviews are needed, thank you if you'd like to share even more!)</p>
              {getSelectedCount() > 3 && (
                <div className="extra-reviews-note margin-t-3">
                  <span>Thank you for the extra reviews! 🙏</span>
                </div>
              )}
            </div>
            
            <button 
              className="confirmation-btn"
              onClick={handleConfirmationClick}
              disabled={getSelectedCount() < 3}
            >
              <span className="btn-icon">{getSelectedCount() >= 3 ? '🎉' : '✅'}</span>
              {getSelectedCount() >= 3 ? 'You\'re ready to claim your reward!' : `Confirm ${getSelectedCount()} Review${getSelectedCount() !== 1 ? 's' : ''} (${getSelectedCount()}/3)`}
            </button>
          </div>
        </section>

        <section className="contact-section" aria-labelledby="contact-title">
          <h2 id="contact-title">Questions or Concerns?</h2>
          <p>If you have any questions about your project or need assistance, please don't hesitate to contact us.</p>
          <div className="contact-info">
            <div className="contact-item">
              <span className="contact-icon">📞</span>
              <a href="tel:+13136316020">313-631-6020</a>
            </div>
            <div className="contact-item">
              <span className="contact-icon">✉️</span>
              <a href="mailto:hi@rcroof.co">hi@rcroof.co</a>
            </div>
          </div>
        </section>

        {/* Client Details Modal */}
        {showDetailsModal && (
          <div 
            className="modal-overlay"
            onClick={handleModalClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <div 
              ref={modalRef}
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2 id="modal-title">Claim Your Reward</h2>
                <button 
                  className="modal-close"
                  onClick={handleModalClose}
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="reward-message" id="modal-description">
                  {getSelectedCount() > 3 ? (
                    <p>🌟 We truly appreciate you going above and beyond with <span className="highlight-number">{getSelectedCount()}</span> reviews! 🌟</p>
                  ) : (
                    <p>Thank you so much for completing<br /><span className="highlight-number">{getSelectedCount()}</span> review{getSelectedCount() !== 1 ? 's' : ''}!</p>
                  )}
                  <p>Please fill in your details below, and one of our representatives will reach out via call or text to send you your <br /><span className="highlight-reward">Amazon gift card</span><br />as a token of our appreciation.</p>
                </div>
                
                <form className="client-details-form">
                  <div className="form-group">
                    <label htmlFor="client-name">* Full Name</label>
                    <input
                      type="text"
                      id="client-name"
                      value={clientDetails.name}
                      onChange={handleNameChange}
                      placeholder="Enter your full name"
                      required
                      aria-describedby="name-help"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="client-phone">* Phone Number</label>
                    <input
                      type="tel"
                      id="client-phone"
                      value={clientDetails.phone}
                      onChange={handlePhoneChange}
                      placeholder="Enter your phone number"
                      maxLength={10}
                      required
                      aria-describedby="phone-help"
                    />
                  </div>
                  
                  <div className="selected-reviews-summary">
                    <h4>⭐ Your Confirmed Reviews ⭐</h4>
                    <ul>
                      {Object.keys(selectedReviews)
                        .filter(key => selectedReviews[key])
                        .map(platform => (
                          <li key={platform}>✅ {platform}</li>
                        ))}
                    </ul>
                  </div>
                  
                  <div className="recaptcha-container">
                    <ReCAPTCHA
                      sitekey={recaptchaConfig.siteKey}
                      onChange={handleRecaptchaChange}
                      onErrored={handleRecaptchaError}
                      theme="dark"
                    />
                    {recaptchaError && (
                      <div className="recaptcha-error">
                        {recaptchaError}
                      </div>
                    )}
                  </div>
                </form>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="modal-submit-btn"
                  onClick={handleDetailsSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Details'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <footer className="footer-note">
          <p className="margin-b-0">
            This page is exclusively for our valued clients. 
            <Link to="/"> Return to our main website</Link>
          </p>
        </footer>
        
      </div>
    </main>
  );
};

export default ClientReviewPage;
